import React, { useCallback, useEffect, useState } from 'react';
import { useLeague, useAuth } from '@shootout-comps/core';
import type { League, LeagueFormat } from '@shootout-comps/types';
import { useLang } from '@shootout-comps/i18n';
import ManageLeagueAdminsDialog from '../../components/ManageLeagueAdminsDialog';
import SendLeagueNotificationDialog from '../../components/SendLeagueNotificationDialog';
import NotableBreaksDialog from '../../components/NotableBreaksDialog';
import EndSeasonDialog from '../../components/EndSeasonDialog';

interface LeagueDashboardPageProps {
  leagueId: string;
  onBack?: () => void;
  onOpenDivision: (divisionId: string) => void;
  onDeleted: () => void;
  onChanged: () => void; // list-level state (e.g. league count) may need a refetch upstream
  onBrowsePublic: () => void;
}

const FORMAT_LABEL: Record<LeagueFormat, string> = { handicap: '🔴 Handicap', scratch: '🎯 Scratch' };

export default function LeagueDashboardPage({ leagueId, onBack, onOpenDivision, onDeleted, onChanged, onBrowsePublic }: LeagueDashboardPageProps) {
  const { fetchLeague, renameLeague, updateLeagueSettings, deleteLeague, addDivision } = useLeague();
  const { user } = useAuth();
  const { t } = useLang();

  const [league, setLeague] = useState<League | null>(null);
  const [error, setError] = useState('');
  const [showAdmins, setShowAdmins] = useState(false);
  const [showNotify, setShowNotify] = useState(false);
  const [showNotableBreaks, setShowNotableBreaks] = useState(false);
  const [showEndSeason, setShowEndSeason] = useState(false);
  const [togglingPublic, setTogglingPublic] = useState(false);
  const [togglingClaimApproval, setTogglingClaimApproval] = useState(false);
  const [savingThreshold, setSavingThreshold] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [showAddDivision, setShowAddDivision] = useState(false);
  const [divName, setDivName] = useState('');
  const [divFormat, setDivFormat] = useState<LeagueFormat>('handicap');
  const [addingDivision, setAddingDivision] = useState(false);
  const [divError, setDivError] = useState('');

  const reload = useCallback(() => {
    fetchLeague(leagueId)
      .then(l => { setLeague(l); setNameDraft(l.name); })
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load league'));
  }, [fetchLeague, leagueId]);

  useEffect(reload, [reload]);

  const isOwned = !!league && league.ownerId === user?._id;
  const isOwnerOrAdmin = isOwned || (!!league && !!user && (league.adminIds ?? []).includes(user._id));

  const handleRename = async () => {
    if (!league || !nameDraft.trim() || nameDraft.trim() === league.name) { setRenaming(false); return; }
    try {
      const updated = await renameLeague(leagueId, nameDraft.trim());
      setLeague(updated);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to rename');
    } finally {
      setRenaming(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true); setError('');
    try {
      await deleteLeague(leagueId);
      onDeleted();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete');
      setDeleting(false);
    }
  };

  const handleTogglePublic = async () => {
    if (!league) return;
    setTogglingPublic(true); setError('');
    try {
      const updated = await updateLeagueSettings(leagueId, { isPublic: !league.isPublic });
      setLeague(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update');
    } finally {
      setTogglingPublic(false);
    }
  };

  const handleToggleClaimApproval = async () => {
    if (!league) return;
    setTogglingClaimApproval(true); setError('');
    try {
      const updated = await updateLeagueSettings(leagueId, { requireClaimApproval: !league.requireClaimApproval });
      setLeague(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update');
    } finally {
      setTogglingClaimApproval(false);
    }
  };

  const handleSaveThreshold = async (raw: string) => {
    if (!league) return;
    const value = raw === '' ? null : Number(raw);
    if (value !== null && (Number.isNaN(value) || value < 0)) return;
    if (value === (league.notableBreakThreshold ?? null)) return;
    setSavingThreshold(true); setError('');
    try {
      const updated = await updateLeagueSettings(leagueId, { notableBreakThreshold: value });
      setLeague(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update');
    } finally {
      setSavingThreshold(false);
    }
  };

  const handleAddDivision = async () => {
    if (!divName.trim()) return;
    setAddingDivision(true); setDivError('');
    try {
      const updated = await addDivision(leagueId, { name: divName.trim(), format: divFormat });
      setLeague(updated);
      setDivName(''); setShowAddDivision(false);
    } catch (e) {
      setDivError(e instanceof Error ? e.message : 'Failed to add division');
    } finally {
      setAddingDivision(false);
    }
  };

  if (error) return <div style={{ maxWidth: '640px', margin: '2rem auto', padding: '1rem', color: '#e74c3c', textAlign: 'center' }}>{error}</div>;
  if (!league) return <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--chalk-dim)' }}>Loading…</div>;

  return (
    <div style={{ maxWidth: '760px', margin: '0 auto', padding: '2rem 1.5rem' }}>
      {onBack && (
        <button onClick={onBack} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', color: 'var(--chalk-dim)', padding: '0.35rem 0.75rem', fontSize: '0.8rem', cursor: 'pointer', marginBottom: '1.25rem' }}>
            ← Leagues
        </button>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
        {renaming ? (
          <input
            autoFocus
            value={nameDraft}
            onChange={e => setNameDraft(e.target.value)}
            onBlur={handleRename}
            onKeyDown={e => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') { setNameDraft(league.name); setRenaming(false); } }}
            style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(1.6rem,5vw,2.2rem)', color: 'var(--gold)', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(201,168,76,0.4)', borderRadius: '6px', padding: '0.2rem 0.5rem' }}
          />
        ) : (
          <h2
            onClick={() => isOwned && setRenaming(true)}
            title={isOwned ? 'Click to rename' : undefined}
            style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(1.6rem,5vw,2.2rem)', color: 'var(--gold)', cursor: isOwned ? 'pointer' : 'default' }}
          >
            🏆 {league.name}
          </h2>
        )}

        {isOwned && (
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              onClick={handleTogglePublic}
              disabled={togglingPublic}
              title={t.togglePublicTooltip}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                background: league.isPublic ? 'rgba(52,152,219,0.14)' : 'rgba(255,255,255,0.07)',
                color: league.isPublic ? '#5dade2' : 'var(--chalk-dim)',
                border: `1px solid ${league.isPublic ? 'rgba(52,152,219,0.4)' : 'rgba(255,255,255,0.18)'}`,
                borderRadius: '20px', padding: '0.35rem 0.7rem 0.35rem 0.6rem',
                fontSize: '0.72rem', fontWeight: 600,
                cursor: togglingPublic ? 'default' : 'pointer', opacity: togglingPublic ? 0.6 : 1,
              }}
            >
              {togglingPublic ? '…' : league.isPublic ? t.publicChipLabel : t.privateChipLabel}
              <span style={{ fontSize: '0.62rem', opacity: 0.65 }}>⇄</span>
            </button>
            <button
              onClick={handleToggleClaimApproval}
              disabled={togglingClaimApproval}
              title={t.toggleClaimApprovalTooltip}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                background: league.requireClaimApproval ? 'rgba(230,126,34,0.14)' : 'rgba(255,255,255,0.07)',
                color: league.requireClaimApproval ? '#e67e22' : 'var(--chalk-dim)',
                border: `1px solid ${league.requireClaimApproval ? 'rgba(230,126,34,0.4)' : 'rgba(255,255,255,0.18)'}`,
                borderRadius: '20px', padding: '0.35rem 0.7rem 0.35rem 0.6rem',
                fontSize: '0.72rem', fontWeight: 600,
                cursor: togglingClaimApproval ? 'default' : 'pointer', opacity: togglingClaimApproval ? 0.6 : 1,
              }}
            >
              {togglingClaimApproval ? '…' : league.requireClaimApproval ? t.claimsApprovalChipLabel : t.claimsInstantChipLabel}
              <span style={{ fontSize: '0.62rem', opacity: 0.65 }}>⇄</span>
            </button>
            <button onClick={() => setShowAdmins(true)} style={{ background: 'rgba(155,89,182,0.12)', color: '#af7ac5', border: '1px solid rgba(155,89,182,0.3)', borderRadius: '6px', padding: '0.4rem 0.8rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>
              👥 Admins
            </button>
            <button onClick={() => setShowNotify(true)} style={{ background: 'rgba(155,89,182,0.12)', color: '#af7ac5', border: '1px solid rgba(155,89,182,0.3)', borderRadius: '6px', padding: '0.4rem 0.8rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>
              📣 Notify
            </button>
            <button onClick={() => setShowNotableBreaks(true)} style={{ background: 'rgba(46,204,113,0.12)', color: '#2ecc71', border: '1px solid rgba(46,204,113,0.3)', borderRadius: '6px', padding: '0.4rem 0.8rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>
              🎯 Notable Breaks
            </button>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: '20px', padding: '0.3rem 0.6rem 0.3rem 0.7rem' }}>
              <span style={{ fontSize: '0.7rem', color: 'var(--chalk-dim)', whiteSpace: 'nowrap' }} title="A break at or above this many points is recorded as a notable break">🎯 Break ≥</span>
              <input
                type="number" min={0}
                defaultValue={league.notableBreakThreshold ?? ''}
                placeholder="off"
                disabled={savingThreshold}
                onBlur={e => handleSaveThreshold(e.target.value)}
                style={{ width: '48px', background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', padding: '0.15rem 0.3rem', color: 'var(--chalk)', fontSize: '0.75rem', textAlign: 'center', outline: 'none' }}
              />
            </div>
            {league.divisions.length > 0 && (
              <button onClick={() => setShowEndSeason(true)} style={{ background: 'rgba(201,168,76,0.12)', color: 'var(--gold)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: '6px', padding: '0.4rem 0.8rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>
                {t.endSeasonBtn}
              </button>
            )}
            <button onClick={() => setConfirmDelete(true)} style={{ background: 'rgba(192,57,43,0.12)', color: '#e74c3c', border: '1px solid rgba(192,57,43,0.3)', borderRadius: '6px', padding: '0.4rem 0.8rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>
              Delete
            </button>
          </div>
        )}
      </div>

      {!isOwned && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1.25rem' }}>
          <span style={{ display: 'inline-block', fontSize: '0.62rem', fontWeight: 700, background: 'rgba(52,152,219,0.12)', color: '#5dade2', border: '1px solid rgba(52,152,219,0.3)', borderRadius: '5px', padding: '0.15rem 0.45rem' }}>
            🤝 Shared
          </span>
          {isOwnerOrAdmin && (
            <>
              <button onClick={() => setShowNotify(true)} style={{ background: 'rgba(155,89,182,0.12)', color: '#af7ac5', border: '1px solid rgba(155,89,182,0.3)', borderRadius: '6px', padding: '0.3rem 0.7rem', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer' }}>
                📣 Notify
              </button>
              <button onClick={() => setShowNotableBreaks(true)} style={{ background: 'rgba(46,204,113,0.12)', color: '#2ecc71', border: '1px solid rgba(46,204,113,0.3)', borderRadius: '6px', padding: '0.3rem 0.7rem', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer' }}>
                🎯 Notable Breaks
              </button>
            </>
          )}
        </div>
      )}

      <button onClick={onBrowsePublic} style={{ display: 'block', background: 'none', border: 'none', color: 'var(--chalk-dim)', fontSize: '0.75rem', cursor: 'pointer', padding: 0, marginBottom: '1rem', textDecoration: 'underline' }}>
        {t.browsePublicLeaguesLink}
      </button>

      <h3 style={{ fontSize: '0.75rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '1.5rem 0 0.75rem', fontWeight: 600 }}>
        Divisions ({league.divisions.length})
      </h3>

      {league.divisions.length === 0 && !showAddDivision && (
        <p style={{ color: 'var(--chalk-dim)', fontSize: '0.85rem', marginBottom: '1rem' }}>
          Add a division to start building teams — e.g. "Premier Division".
        </p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '1.25rem' }}>
        {league.divisions.map(d => (
          <div
            key={d._id}
            onClick={() => onOpenDivision(d._id)}
            style={{ display: 'flex', alignItems: 'center', gap: '1rem', background: 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '0.9rem 1.1rem', cursor: 'pointer' }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{d.name}</div>
              <div style={{ fontSize: '0.73rem', color: 'var(--chalk-dim)', marginTop: '0.2rem' }}>
                {FORMAT_LABEL[d.format]} · {d.teams.length} team{d.teams.length === 1 ? '' : 's'} · best of {d.framesPerFixture} per fixture
              </div>
            </div>
            <span style={{ color: 'var(--chalk-dim)' }}>›</span>
          </div>
        ))}
      </div>

      {showAddDivision ? (
        <div style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          <input
            value={divName}
            onChange={e => setDivName(e.target.value)}
            placeholder="Division name (e.g. Premier Division)"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '0.55rem 0.75rem', color: 'var(--chalk)', fontSize: '0.85rem', outline: 'none' }}
          />
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {(['handicap', 'scratch'] as LeagueFormat[]).map(f => (
              <button
                key={f}
                onClick={() => setDivFormat(f)}
                style={{ flex: 1, background: divFormat === f ? 'rgba(201,168,76,0.15)' : 'rgba(255,255,255,0.05)', border: `1px solid ${divFormat === f ? 'rgba(201,168,76,0.4)' : 'rgba(255,255,255,0.12)'}`, borderRadius: '8px', padding: '0.5rem', color: divFormat === f ? 'var(--gold)' : 'var(--chalk-dim)', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}
              >
                {FORMAT_LABEL[f]}
              </button>
            ))}
          </div>
          {divError && <p style={{ color: '#e74c3c', fontSize: '0.78rem', margin: 0 }}>{divError}</p>}
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={() => setShowAddDivision(false)} style={{ flex: 1, background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '0.55rem', color: 'var(--chalk-dim)', fontSize: '0.82rem', cursor: 'pointer' }}>Cancel</button>
            <button onClick={handleAddDivision} disabled={addingDivision || !divName.trim()} style={{ flex: 2, background: 'linear-gradient(135deg, var(--gold), var(--gold-light))', color: 'var(--baize-dark)', border: 'none', borderRadius: '8px', padding: '0.55rem', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', opacity: addingDivision || !divName.trim() ? 0.6 : 1 }}>
              {addingDivision ? '…' : 'Add Division'}
            </button>
          </div>
        </div>
      ) : (
        <button onClick={() => setShowAddDivision(true)} style={{ background: 'rgba(201,168,76,0.1)', color: 'var(--gold)', border: '1px dashed rgba(201,168,76,0.35)', borderRadius: '10px', padding: '0.75rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', width: '100%' }}>
          + Add Division
        </button>
      )}

      {showAdmins && (
        <ManageLeagueAdminsDialog leagueId={leagueId} leagueName={league.name} onClose={() => setShowAdmins(false)} />
      )}

      {showNotify && (
        <SendLeagueNotificationDialog league={league} onClose={() => setShowNotify(false)} onSent={setLeague} />
      )}

      {showNotableBreaks && (
        <NotableBreaksDialog leagueId={leagueId} leagueName={league.name} onClose={() => setShowNotableBreaks(false)} />
      )}

      {showEndSeason && (
        <EndSeasonDialog league={league} onClose={() => setShowEndSeason(false)} onCommitted={reload} />
      )}

      {confirmDelete && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }} onClick={e => { if (e.target === e.currentTarget) setConfirmDelete(false); }}>
          <div style={{ background: 'linear-gradient(135deg, var(--baize), var(--felt))', border: '2px solid rgba(192,57,43,0.4)', borderRadius: '14px', padding: '1.75rem', width: '100%', maxWidth: '400px' }}>
            <h3 style={{ fontFamily: 'Bebas Neue', fontSize: '1.4rem', color: '#e74c3c', marginBottom: '0.75rem' }}>Delete "{league.name}"?</h3>
            <p style={{ color: 'var(--chalk-dim)', fontSize: '0.85rem', marginBottom: '1.25rem' }}>This can't be undone — all divisions, teams and rosters will be permanently deleted.</p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={() => setConfirmDelete(false)} style={{ flex: 1, background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '0.6rem', color: 'var(--chalk-dim)', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleDelete} disabled={deleting} style={{ flex: 1, background: '#c0392b', border: 'none', borderRadius: '8px', padding: '0.6rem', color: '#fff', fontWeight: 700, cursor: 'pointer', opacity: deleting ? 0.6 : 1 }}>
                {deleting ? '…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
